import { FC } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { Member } from '@/entities/vfo/types';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { Tabs } from '@/features/vfo/uikit/Tabs';
import { ApiCaseResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { Button } from '@/shared/uikitV2/Button';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VfoChatInfoModalProps } from '@/widgets/modals/types';

import { VfoModalContainer } from '../containers/VfoModalContainer';

import { FilesTab } from './FilesTab';
import { MembersTab } from './MembersTab';

export const ChatInfoModal: FC<VfoChatInfoModalProps> = ({ chat, member, onOpenCase }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChatInfo' });

  const router = useRouter();
  const modals = useModalsStore();
  const { findByChatId } = useCases();
  const { getDocumentsByCaseId } = useDocuments();
  const { tag } = useI18nValues();

  const caseData = findByChatId(chat.id);
  const files = caseData?.id ? getDocumentsByCaseId(caseData.id) : [];

  const props:
    | {
        type: 'case';
        caseData?: ApiCaseResponseV2;
      }
    | {
        type: 'member';
        member: Member;
      } = member ? { type: 'member', member } : { type: 'case', caseData };

  const title = member
    ? getPersonFullName(member, 'display')
    : chat.title ?? caseData?.title ?? 'Chat Info';

  const subTitle = member?.isFounder ? t('founder') : member?.tag ? tag(member?.tag) : '';

  return (
    <VfoModalContainer title={title} subTitle={subTitle} {...props}>
      <ChatContent>
        {member ? (
          <>
            <b>{t('basicInfo')}</b>
            <InfoWrapper>
              {member.createdAt && (
                <InfoBlock>
                  <TextGrey>{t('startDate')}</TextGrey>
                  <span>{dates.formatLongDate2(member.createdAt)}</span>
                </InfoBlock>
              )}
              {member.lastActiveDate && (
                <InfoBlock>
                  <TextGrey>{t('lastActive')}</TextGrey>
                  <span>{dates.formatLongDate2(member.lastActiveDate)}</span>
                </InfoBlock>
              )}
            </InfoWrapper>
          </>
        ) : (
          <p>{caseData?.description ?? chat.description}</p>
        )}
        {member || caseData ? (
          <Button
            type="text"
            size="small"
            style={{
              height: 'fit-content',
              width: 'fit-content',
              padding: 0,
            }}
            onClick={() => {
              if (member) {
                modals.closeModal();
                modals.openModal({
                  type: 'VfoProfileModal',
                  initialMember: member,
                  onOpenCase,
                });
                return;
              }
              if (!caseData) return;
              modals.closeModal();
              router.push(`/vfo/case/${caseData.id}`);
              onOpenCase?.();
            }}
          >
            {t(member ? 'viewFullProfile' : 'openCase')}
          </Button>
        ) : (
          <span style={{ color: COLORS.systemFlame200 }}>{t('caseDeleted')}</span>
        )}
      </ChatContent>
      {!member && (
        <StyledTabs
          items={[
            {
              label: (
                <>
                  {t('members')}
                  <Counter>{chat.participants.length}</Counter>
                </>
              ),
              key: 'members',
              forceRender: true,
              children: (
                <MembersTab
                  onOpenCase={onOpenCase}
                  participants={chat.participants}
                  caseData={caseData}
                />
              ),
            },
            {
              label: (
                <>
                  {t('files')} <Counter>{files.length}</Counter>
                </>
              ),
              key: 'files',
              forceRender: true,
              children: <FilesTab files={files} isCaseDeleted={!member && !caseData} />,
            },
          ]}
        ></StyledTabs>
      )}

      {member && (
        <>
          <FilesTitle>{t('files')}</FilesTitle>
          <FilesTab files={files} />
        </>
      )}
    </VfoModalContainer>
  );
};

const TextGrey = styled.span`
  color: ${COLORS.systemDark200};
`;

const StyledTabs = styled(Tabs)`
  &&& {
    margin-top: 24px;
  }
`;

const Counter = styled.div`
  padding: 2px 8px;
  background: ${COLORS.systemDark50A};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.systemDark300};
  border-radius: 6px;
  width: 23px;
  height: 20px;
  font-size: 12px;
  font-weight: 400;
`;

const FilesTitle = styled.b`
  margin: 24px 0 16px;
`;

const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBlock = styled.div`
  gap: 16px;
  display: flex;
`;

const InfoWrapper = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;
